import React, { useState, useEffect, useCallback } from 'react';
import chemicalService from '../../../services/chemicalService';
import CostingFormModal from '../components/CostingFormModal';

const PAGE_SIZE = 10;

const CostingTab = () => {
  const [costings, setCostings] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: PAGE_SIZE, total: 0, totalPages: 1 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);

  const [showFormModal, setShowFormModal] = useState(false);
  const [editingCosting, setEditingCosting] = useState(null);
  const [viewCosting, setViewCosting] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => { setPage(1); }, [debouncedSearch]);

  const fetchCostings = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page, limit: PAGE_SIZE };
      if (debouncedSearch.trim()) params.search = debouncedSearch.trim();
      const res = await chemicalService.getCostings(params);
      setCostings(res.data || []);
      setPagination(res.pagination || { page: 1, limit: PAGE_SIZE, total: 0, totalPages: 1 });
    } catch (err) {
      console.error('Error fetching costings:', err);
      alert('❌ ' + (err.response?.data?.message || 'Failed to load costings'));
    } finally {
      setLoading(false);
    }
  }, [page, debouncedSearch]);

  useEffect(() => { fetchCostings(); }, [fetchCostings]);

  const handleAdd = () => { setEditingCosting(null); setShowFormModal(true); };
  const handleEdit = async (c) => {
    try {
      const res = await chemicalService.getCostingById(c.id);
      setEditingCosting(res.data);
      setShowFormModal(true);
    } catch (err) {
      alert('❌ Failed to load costing');
    }
  };
  const handleView = async (c) => {
    try {
      const res = await chemicalService.getCostingById(c.id);
      setViewCosting(res.data);
    } catch (err) {
      alert('❌ Failed to load costing');
    }
  };
  const handleDelete = async (c) => {
    if (!window.confirm(`Delete costing "${c.code || c.id}"?\n\nCannot be undone.`)) return;
    try {
      await chemicalService.deleteCosting(c.id);
      alert('✅ Costing deleted.');
      if (costings.length === 1 && page > 1) setPage(page - 1); else fetchCostings();
    } catch (err) {
      alert('❌ ' + (err.response?.data?.message || 'Failed to delete'));
    }
  };
  const handleSaved = () => { setShowFormModal(false); setEditingCosting(null); fetchCostings(); };

  const fmtMoney = (n) => 'Rs ' + Math.round(parseFloat(n) || 0).toLocaleString('en-PK');
  const fmtNum = (n) => (parseFloat(n) || 0).toLocaleString('en-PK');
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

  return (
    <div style={{ padding: '24px 30px 40px' }}>
      <div style={styles.toolbar}>
        <div style={styles.searchWrap}>
          <span style={styles.searchIcon}>⌕</span>
          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search code or product..." style={styles.searchInput} />
        </div>
        <button onClick={handleAdd} style={styles.addButton}>+ New Costing</button>
      </div>

      <div style={styles.tableWrap}>
        <table style={styles.table}>
          <thead>
            <tr style={styles.headerRow}>
              <th style={styles.th}>Code</th>
              <th style={styles.th}>Product</th>
              <th style={{ ...styles.th, textAlign: 'right' }}>Output (Kg)</th>
              <th style={{ ...styles.th, textAlign: 'right' }}>Total Cost</th>
              <th style={{ ...styles.th, textAlign: 'right' }}>Cost/Kg</th>
              <th style={{ ...styles.th, textAlign: 'right' }}>Selling/Kg</th>
              <th style={styles.th}>Date</th>
              <th style={{ ...styles.th, textAlign: 'center' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="8" style={styles.noData}>⏳ Loading...</td></tr>
            ) : costings.length === 0 ? (
              <tr><td colSpan="8" style={styles.noData}><div style={{ fontSize: 40, marginBottom: 8 }}>🧮</div>No costings yet — click "+ New Costing"</td></tr>
            ) : (
              costings.map((c) => (
                <tr key={c.id} style={styles.row}>
                  <td style={styles.td}><strong>{c.code || `#${c.id}`}</strong></td>
                  <td style={styles.td}>{c.product_name || '—'}{c.formula_code && <div style={{ fontSize: 11, color: '#95a5a6' }}>Recipe: {c.formula_code}</div>}</td>
                  <td style={{ ...styles.td, textAlign: 'right' }}>{fmtNum(c.yield_qty)}</td>
                  <td style={{ ...styles.td, textAlign: 'right' }}>{fmtMoney(c.total_cost)}</td>
                  <td style={{ ...styles.td, textAlign: 'right' }}><strong style={{ color: '#8e44ad' }}>{fmtMoney(c.cost_per_kg)}</strong></td>
                  <td style={{ ...styles.td, textAlign: 'right' }}>{c.selling_price_per_kg ? <strong style={{ color: '#27ae60' }}>{fmtMoney(c.selling_price_per_kg)}</strong> : '—'}</td>
                  <td style={styles.td}>{fmtDate(c.created_at)}</td>
                  <td style={{ ...styles.td, textAlign: 'center' }}>
                    <div style={{ display: 'inline-flex', gap: '6px' }}>
                      <button onClick={() => handleView(c)} style={styles.viewButton} title="View">👁️</button>
                      <button onClick={() => handleEdit(c)} style={styles.editButton} title="Edit">✏️</button>
                      <button onClick={() => handleDelete(c)} style={styles.deleteButton} title="Delete">🗑️</button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {!loading && pagination.total > 0 && (
        <div style={styles.paginationWrap}>
          <div style={styles.paginationInfo}>
            Showing {(pagination.page - 1) * pagination.limit + 1}–{Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}
          </div>
          <div style={styles.paginationButtons}>
            <button onClick={() => setPage(page - 1)} disabled={page === 1} style={{ ...styles.pageButton, ...(page === 1 ? styles.pageButtonDisabled : {}) }}>‹ Prev</button>
            <span style={styles.pageIndicator}>Page {pagination.page} of {pagination.totalPages}</span>
            <button onClick={() => setPage(page + 1)} disabled={page >= pagination.totalPages} style={{ ...styles.pageButton, ...(page >= pagination.totalPages ? styles.pageButtonDisabled : {}) }}>Next ›</button>
          </div>
        </div>
      )}

      {showFormModal && (
        <CostingFormModal costing={editingCosting} onClose={() => { setShowFormModal(false); setEditingCosting(null); }} onSaved={handleSaved} />
      )}

      {viewCosting && (
        <div style={styles.modalOverlay} onClick={() => setViewCosting(null)}>
          <div style={styles.viewModal} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>🧮 {viewCosting.code || `Costing #${viewCosting.id}`}</h2>
              <button onClick={() => setViewCosting(null)} style={styles.closeButton}>✕</button>
            </div>
            <div style={{ padding: '20px 30px' }}>
              <div style={styles.viewGrid}>
                <div><span style={styles.viewLabel}>Product</span>{viewCosting.product_name || '—'}</div>
                <div><span style={styles.viewLabel}>Recipe</span>{viewCosting.formula_code || '—'}</div>
                <div><span style={styles.viewLabel}>Multiplier</span>{fmtNum(viewCosting.multiplier)}</div>
                <div><span style={styles.viewLabel}>Est. Output</span>{fmtNum(viewCosting.yield_qty)} Kg</div>
              </div>

              <table style={styles.viewItems}>
                <thead>
                  <tr>
                    <th style={styles.viewItemTh}>Ingredient</th>
                    <th style={{ ...styles.viewItemTh, textAlign: 'right' }}>Qty</th>
                    <th style={styles.viewItemTh}>Unit</th>
                    <th style={{ ...styles.viewItemTh, textAlign: 'right' }}>Rate</th>
                    <th style={{ ...styles.viewItemTh, textAlign: 'right' }}>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {(viewCosting.items || []).map((it, i) => (
                    <tr key={i}>
                      <td style={styles.viewItemTd}>{it.product_name || it.ingredient_name || '—'}</td>
                      <td style={{ ...styles.viewItemTd, textAlign: 'right' }}>{fmtNum(it.quantity)}</td>
                      <td style={styles.viewItemTd}>{it.unit}</td>
                      <td style={{ ...styles.viewItemTd, textAlign: 'right' }}>{fmtMoney(it.rate)}</td>
                      <td style={{ ...styles.viewItemTd, textAlign: 'right' }}>{fmtMoney(it.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div style={styles.resultsBox}>
                <div style={styles.resultRow}><span>Raw Material Cost</span><span>{fmtMoney(viewCosting.raw_material_cost)}</span></div>
                {parseFloat(viewCosting.overhead_cost) > 0 && <div style={styles.resultRow}><span>Overhead</span><span>{fmtMoney(viewCosting.overhead_cost)}</span></div>}
                {parseFloat(viewCosting.commission_amount) > 0 && (
                  <div style={styles.resultRow}>
                    <span>Commission{viewCosting.commission_percent ? ` (${parseFloat(viewCosting.commission_percent)}%)` : ''}{viewCosting.commission_per_kg ? ` (${fmtMoney(viewCosting.commission_per_kg)}/kg)` : ''}</span>
                    <span>{fmtMoney(viewCosting.commission_amount)}</span>
                  </div>
                )}
                <div style={styles.resultRowTotal}><span>Total Cost</span><span>{fmtMoney(viewCosting.total_cost)}</span></div>
                <div style={styles.resultRowKg}><span>Cost / Kg</span><span>{fmtMoney(viewCosting.cost_per_kg)}</span></div>
                {viewCosting.selling_price_per_kg && parseFloat(viewCosting.margin_percent) > 0 && (
                  <div style={styles.resultRowSelling}><span>Suggested Selling / Kg ({parseFloat(viewCosting.margin_percent)}% margin)</span><span>{fmtMoney(viewCosting.selling_price_per_kg)}</span></div>
                )}
              </div>

              {viewCosting.notes && (
                <div style={{ marginTop: 14, padding: 12, background: '#f8f9fa', borderRadius: 8, fontSize: 13, color: '#555' }}>
                  <strong>Notes:</strong> {viewCosting.notes}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  toolbar: { background: 'white', padding: '16px 20px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' },
  searchWrap: { position: 'relative' },
  searchIcon: { position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#95a5a6', fontSize: '16px' },
  searchInput: { padding: '10px 12px 10px 36px', border: '2px solid #e0e0e0', borderRadius: '8px', fontSize: '14px', width: '280px', fontFamily: 'inherit' },
  addButton: { padding: '10px 20px', background: '#8e44ad', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' },
  tableWrap: { background: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', overflow: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse' },
  headerRow: { background: '#f8f9fa' },
  th: { padding: '14px 16px', textAlign: 'left', fontSize: '13px', fontWeight: '700', color: '#2c3e50', borderBottom: '2px solid #e0e0e0', whiteSpace: 'nowrap' },
  row: { borderBottom: '1px solid #f0f0f0' },
  td: { padding: '12px 16px', fontSize: '14px', color: '#2c3e50' },
  noData: { padding: '60px 20px', textAlign: 'center', color: '#95a5a6', fontSize: '14px' },
  viewButton: { padding: '6px 10px', background: '#eafaf1', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px' },
  editButton: { padding: '6px 10px', background: '#3498db', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px' },
  deleteButton: { padding: '6px 10px', background: '#e74c3c', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px' },
  paginationWrap: { marginTop: '16px', background: 'white', padding: '14px 20px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' },
  paginationInfo: { fontSize: '13px', color: '#7f8c8d' },
  paginationButtons: { display: 'flex', gap: '6px', alignItems: 'center' },
  pageButton: { padding: '8px 14px', background: 'white', color: '#2c3e50', border: '2px solid #e0e0e0', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' },
  pageButtonDisabled: { opacity: 0.4, cursor: 'not-allowed' },
  pageIndicator: { padding: '0 12px', fontSize: '13px', color: '#2c3e50', fontWeight: '600' },
  modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' },
  viewModal: { background: 'white', borderRadius: '12px', width: '95%', maxWidth: '720px', maxHeight: '92vh', overflow: 'auto', boxShadow: '0 10px 40px rgba(0,0,0,0.2)' },
  modalHeader: { padding: '20px 30px', borderBottom: '2px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, background: 'white', zIndex: 10 },
  modalTitle: { fontSize: '20px', fontWeight: '700', color: '#2c3e50', margin: 0 },
  closeButton: { padding: '8px 16px', background: '#f0f0f0', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '20px', fontWeight: '700', color: '#666' },
  viewGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '10px 20px', marginBottom: '16px', fontSize: '14px', color: '#2c3e50' },
  viewLabel: { display: 'block', fontSize: '11px', fontWeight: '700', color: '#95a5a6', textTransform: 'uppercase', marginBottom: '2px' },
  viewItems: { width: '100%', borderCollapse: 'collapse', border: '1px solid #eee', borderRadius: '8px', overflow: 'hidden', marginBottom: '16px' },
  viewItemTh: { padding: '8px 10px', textAlign: 'left', fontSize: '11px', fontWeight: '700', color: '#7f8c8d', textTransform: 'uppercase', background: '#f8f9fa', borderBottom: '1px solid #eee' },
  viewItemTd: { padding: '8px 10px', fontSize: '13px', color: '#2c3e50', borderBottom: '1px solid #f4f4f4' },
  resultsBox: { background: '#f4f0fa', borderRadius: '10px', padding: '16px 20px' },
  resultRow: { display: 'flex', justifyContent: 'space-between', fontSize: '14px', color: '#7f8c8d', padding: '4px 0' },
  resultRowTotal: { display: 'flex', justifyContent: 'space-between', fontSize: '16px', fontWeight: '700', color: '#2c3e50', padding: '8px 0 4px', borderTop: '1px solid #d5c8e8', marginTop: '4px' },
  resultRowKg: { display: 'flex', justifyContent: 'space-between', fontSize: '16px', fontWeight: '700', color: '#8e44ad', padding: '4px 0' },
  resultRowSelling: { display: 'flex', justifyContent: 'space-between', fontSize: '16px', fontWeight: '700', color: '#27ae60', padding: '6px 0', borderTop: '1px dashed #d5c8e8', marginTop: '4px' },
};

export default CostingTab;